import { useMemo, useState } from 'react';
import { SliderModal } from '@components/molecules/SlideOver';
import { Task } from '@models/task.model';
import { TaskDetailHeader } from './components/TaskDetailHeader';
import { TaskDetailCrews } from './components/TaskDetailCrews';
import { TaskDetailDocuments } from './components/TaskDetailDocuments';
import { TaskDetailEvidence } from './components/TaskDetailEvidence';
import { TaskDetailInventory } from './components/TaskDetailInventory';
import { Project } from '@models/project.model';
import { progressValueCalculation } from './utils/progressDateCalculation';
import { ProjectFeed } from '@pages/projects/components/ProjectFeed';
import { useProjectFeedByTask } from '@pages/projects/components/ProjectFeed/hooks';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import { Card } from '@components/atoms/Card';
import { Badge } from '@components/atoms/Badge';

interface TaskDetailModalProps {
  task: Task | null;
  /**
   * Only `projectId` is read, so callers that hold a trimmed-down project (the
   * crews page carries just id + name on each task) can open this too.
   */
  project?: Partial<Project>;
  isTaskDetailModal: boolean;
  setIsTaskDetailModal: (isOpen: boolean) => void;
}

export const TaskDetailModal = ({
  task,
  project,
  isTaskDetailModal,
  setIsTaskDetailModal,
}: TaskDetailModalProps) => {
  const [countFeed, setCountFeed] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const progressValue = useMemo(() => {
    return progressValueCalculation(task as Task);
  }, [task]);

  // Use ProjectFeed hook for task activities
  const { activities, paginationActions, totalCount } = useProjectFeedByTask({
    projectId: project?.projectId || '',
    taskId: task?.taskId || '',
  });


  return (
    <SliderModal
      title="Task Details"
      isOpen={isTaskDetailModal}
      closeSlider={() => setIsTaskDetailModal(false)}>
      {/* Story-066: SliderModal has no role="dialog" — task-detail is this panel's only stable selector. */}
      <div className="flex flex-col gap-6 pb-6 mt-6" data-testid="task-detail">
        {/* Header Section */}
        <TaskDetailHeader task={task} progressValue={progressValue} />

        {/* Crews Section */}
        <TaskDetailCrews task={task} />

        {/* Documents Section */}
        <TaskDetailDocuments task={task} />

        {/* Evidence Section (story-042, U77) */}
        <TaskDetailEvidence task={task} />

        {/* Inventory Section */}
        <TaskDetailInventory task={task} />

        {/* Activities Section — also the status-history list task-status-transitions.e2e.ts
            (story-069) asserts against; StatusHistory-driven entries render here via
            ActivityTaskStatus. */}
        <Card className="space-y-4" data-testid="task-detail-status-history">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Activities
              </h3>
              <Badge variant="secondary" size="sm">
                {countFeed}
              </Badge>
            </div>

            <div
              className="cursor-pointer"
              onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? (
                <ExpandMoreOutlinedIcon />
              ) : (
                <ExpandLessOutlinedIcon />
              )}
            </div>
          </div>
          {!isCollapsed && (
            <ProjectFeed
              activities={activities}
              paginationActions={paginationActions}
              totalCount={totalCount}
              setCountFeed={setCountFeed}
            />
          )}
        </Card>

        {/* Empty State */}
        {!task && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">No task selected</p>
          </div>
        )}
      </div>
    </SliderModal>
  );
};
